import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Clock, Edit, Eye, Calendar } from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';

interface ScheduledPost {
  id: string;
  title: string;
  slug: string;
  scheduled_publish_at: string;
  author_name: string;
  categories: string[] | null;
}

function CountdownBadge({ targetDate }: { targetDate: string }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  const target = new Date(targetDate);
  if (isPast(target)) {
    return <Badge variant="default" className="bg-amber-500 text-white">Publishing soon…</Badge>;
  }

  return (
    <Badge variant="outline" className="font-mono text-xs">
      <Clock className="h-3 w-3 mr-1" />
      {formatDistanceToNow(target, { addSuffix: false })}
    </Badge>
  );
}

export default function ScheduledPostsPanel() {
  const navigate = useNavigate();

  const { data: posts, isLoading } = useQuery({
    queryKey: ['scheduled-posts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('id, title, slug, scheduled_publish_at, author_name, categories, status')
        .not('scheduled_publish_at', 'is', null)
        .in('status', ['scheduled', 'draft'])
        .order('scheduled_publish_at', { ascending: true });

      if (error) throw error;
      return (data || []) as (ScheduledPost & { status: string })[];
    },
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" /> Scheduled Posts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const upcoming = (posts || []).filter((p) => !isPast(new Date(p.scheduled_publish_at)));
  const overdue = (posts || []).filter((p) => isPast(new Date(p.scheduled_publish_at)));

  if (!posts || posts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calendar className="h-5 w-5" /> Scheduled Posts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm text-center py-4">
            No posts scheduled. Use the "Schedule" button when creating a post.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="h-5 w-5" />
          Scheduled Posts
          <Badge variant="secondary" className="ml-auto">{posts.length}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {overdue.length > 0 && (
          <p className="text-xs text-amber-600 font-medium">
            ⏳ {overdue.length} post(s) past scheduled time — will auto-publish within 15 min
          </p>
        )}
        {[...overdue, ...upcoming].map((post) => (
          <div
            key={post.id}
            className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card hover:bg-accent/30 transition-colors"
          >
            <div className="min-w-0 flex-1">
              <p className="font-medium text-sm truncate">{post.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground">
                  {format(new Date(post.scheduled_publish_at), 'MMM d, yyyy · h:mm a')}
                </span>
                <CountdownBadge targetDate={post.scheduled_publish_at} />
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/admin/content/blog/edit/${post.id}`)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
